/**
 * rcSessionQuery — TanStack Query factory for RC session metadata.
 *
 * Phase 7 v2 Task 2. Used by the /rc/session/$sessionId route loader
 * to know if the session exists before mounting <GameSessionRoom>.
 *
 * The identity JWT is fetched separately inside GameSessionRoom
 * (post-mount) so we keep tokens out of the route pre-render path.
 */
import { queryOptions } from '@tanstack/react-query';
import { fetchApi } from './client.js';

export interface RcSessionMetadata {
  id: string;
  classroom_id: string | null;
  mission_id: string;
  game_id: string;
  tutor_id: string;
  status: 'waiting' | 'open' | 'playing' | 'paused' | 'completed';
}

export function rcSessionQuery(sessionId: string) {
  return queryOptions({
    queryKey: ['rc-session', sessionId] as const,
    queryFn: async (): Promise<{ ok: true; data: RcSessionMetadata }> => {
      const res = await fetchApi<RcSessionMetadata>(
        `/api/rc/sessions/${encodeURIComponent(sessionId)}`,
      );
      if (!res.ok) {
        throw new Error('Session metadata not found');
      }
      // res is { ok: true, data: RcSessionMetadata } — rewrap to match the
      // return type. Caller unwraps via queryClient.getQueryData().
      return { ok: true, data: res.data };
    },
    staleTime: 0, // session status can change rapidly
  });
}
